// src/services/accountDeletionRequests.ts
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../lib/firebase';

type AccountDeletionRequestInput = {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  reason?: string | null;
};

type AccountDeletionCallableResponse = {
  ok?: boolean;
  status?: 'requested' | 'scheduled' | 'processing' | 'completed' | 'cancelled' | 'failed';
  alreadyExists?: boolean;
  scheduledDeletionAt?: number | null;
  graceDays?: number;
  error?: string;
};

const cleanString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const cleaned = value.trim();
  return cleaned.length > 0 ? cleaned : null;
};

/**
 * Creates a self-serve account deletion request through Cloud Functions.
 *
 * Important:
 * - The client never hard-deletes user data directly.
 * - The callable verifies auth.uid server-side.
 * - The backend schedules and later processes deletion/anonymization safely.
 */
export async function requestAccountDeletion(input: AccountDeletionRequestInput): Promise<AccountDeletionCallableResponse> {
  const uid = cleanString(input.uid);

  if (!uid) {
    throw new Error('Missing user id for account deletion request.');
  }

  const authUser = auth.currentUser;
  const callable = httpsCallable<
    {
      uid: string;
      email?: string | null;
      displayName?: string | null;
      reason?: string | null;
      clientSubmittedAt: number;
    },
    AccountDeletionCallableResponse
  >(functions, 'requestAccountDeletionAndSchedule');

  const result = await callable({
    uid,
    email: cleanString(input.email) || cleanString(authUser?.email),
    displayName: cleanString(input.displayName) || cleanString(authUser?.displayName),
    reason: cleanString(input.reason),
    clientSubmittedAt: Date.now(),
  });

  if (result.data?.ok === false) {
    throw new Error(result.data.error || 'Account deletion request failed.');
  }

  return result.data || { ok: true, status: 'requested' };
}
